// Shared types and interfaces for accounting views
// Extracted from stores/AccountingStore to avoid dependencies

// Enums
export enum InvoiceStatus {
  DRAFT = "draft",
  SENT = "sent",
  PAID = "paid",
  OVERDUE = "overdue",
  CANCELLED = "cancelled",
}

export enum BillStatus {
  DRAFT = "draft",
  RECEIVED = "received",
  PAID = "paid",
  OVERDUE = "overdue",
  CANCELLED = "cancelled",
}

export enum CustomerStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

export enum SupplierStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  PENDING = "pending",
  BLOCKED = "blocked",
}

export enum PaymentMethod {
  CASH = "cash",
  CHECK = "check",
  CREDIT_CARD = "credit_card",
  BANK_TRANSFER = "bank_transfer",
  OTHER = "other",
}

export enum PaymentStatus {
  PENDING = "pending",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
}

export enum AccountType {
  ASSET = "asset",
  LIABILITY = "liability",
  EQUITY = "equity",
  REVENUE = "revenue",
  EXPENSE = "expense",
}

// Interfaces
export interface Invoice {
  id: number;
  invoiceNumber: string;
  customerId: number;
  customerName?: string; // Optional since it's not in the API response but may be populated by UI
  invoiceDate: string; // YYYY-MM-DD format
  invoiceTime: string; // HH:MM:SS format
  dueDate: string; // YYYY-MM-DD format
  dueTime: string; // HH:MM:SS format
  status: InvoiceStatus;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  amountPaid: number;
  amountDue: number;
  currency: string;
  exchangeRate: number;
  paymentTerms: number; // days
  notes?: string;
  internalNotes?: string;
  createdDate: string; // YYYY-MM-DD format
  createdTime: string; // HH:MM:SS format
  updatedDate: string; // YYYY-MM-DD format
  updatedTime: string; // HH:MM:SS format
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
  lineItems?: InvoiceLineItem[]; // Optional since it's not in the API response but may be populated separately
}

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface Bill {
  id: string;
  billNumber: string;
  supplierId: string;
  supplierName: string;
  vendorId: string;
  referenceNumber: string;
  billDate: string; // YYYY-MM-DD format
  issueDate: string; // YYYY-MM-DD format
  dueDate: string; // YYYY-MM-DD format
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  status: BillStatus;
  notes?: string;
  lineItems: BillLineItem[];
  createdAt: string;
  updatedAt: string;
}

export interface BillLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  totalAmount: number;
  total: number;
}

export interface Customer {
  id: string;
  customerCode: string;
  companyName: string;
  contactFirstName: string;
  contactLastName: string;
  email: string;
  phone: string;
  mobile?: string;
  website?: string;
  billingAddressLine1: string;
  billingAddressLine2?: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingCountry: string;
  shippingAddressLine1: string;
  shippingAddressLine2?: string;
  shippingCity: string;
  shippingState: string;
  shippingPostalCode: string;
  shippingCountry: string;
  taxNumber: string;
  paymentTerms: number; // days
  creditLimit: number;
  isActive: boolean;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Supplier {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  website?: string;
  taxId?: string;
  paymentTerms: string;
  balance: number;
  status: SupplierStatus;
  billingAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  shippingAddress?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  contactPerson?: {
    name: string;
    email: string;
    phone: string;
    position: string;
  };
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Payment {
  id: string;
  paymentNumber: string;
  paymentType: "customer_payment" | "supplier_payment";
  amount: number;
  paymentDate: string; // YYYY-MM-DD format
  paymentTime: string; // HH:MM:SS format
  paymentMethod: PaymentMethod;
  referenceNumber?: string;
  bankAccount?: string;
  currency: string;
  exchangeRate: number;
  customerId?: number | null;
  supplierId?: number | null;
  invoiceId?: number | null;
  billId?: number | null;
  status: PaymentStatus;
  notes?: string;
  createdDate: string;
  createdTime: string;
  updatedDate: string;
  updatedTime: string;
  created_at: string;
  updated_at: string;
  // Legacy fields for backward compatibility
  type?: "received" | "sent";
  reference?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ChartOfAccount {
  id: string;
  accountNumber: string;
  accountName: string;
  accountType: AccountType;
  parentAccountId?: string;
  balance: number;
  isActive: boolean;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface JournalEntry {
  id: string;
  entryNumber: string;
  entryDate: string; // YYYY-MM-DD format
  description: string;
  reference?: string;
  totalDebit: number;
  totalCredit: number;
  status: "draft" | "posted" | "reversed";
  lineItems: JournalLineItem[];
  createdAt: string;
  updatedAt: string;
}

export interface JournalLineItem {
  id: string;
  accountId: string;
  accountName: string;
  description: string;
  debitAmount: number;
  creditAmount: number;
}

export interface AccountingSummary {
  totalReceivables: number;
  totalPayables: number;
  overdueReceivables: number;
  overduePayables: number;
  currentMonthRevenue: number;
  currentMonthExpenses: number;
  netIncome: number;
  cashBalance: number;
}

export interface DashboardData {
  totalReceivables: number;
  outstandingInvoicesCount: number;
  totalPayables: number;
  outstandingBillsCount: number;
  overdueInvoicesAmount: number;
  overdueInvoicesCount: number;
  billsDueSoonAmount: number;
  billsDueSoonCount: number;
  currentMonthRevenue: number;
  currentMonthExpenses: number;
  netIncome: number;
  cashBalance: number;
}
